import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTheme } from '../contexts/ThemeContext';
import { useProgress } from '../components/TopProgressBar';
import Button from '../components/Button';
import { playLoginSound } from '../hooks/useSound';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const { start: progressStart, done: progressDone, showLoader, hideLoader } = useProgress();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(username, password);
      playLoginSound();
      showLoader('Entrando...');
      progressStart();
      navigate('/'); // RootRedirect decide según el rol
    } catch (error) {
      // Error ya manejado en el store
    }
  };

  return (
    <div className="min-h-screen flex bg-brand-bg">
      {/* Panel izquierdo — color fijo siempre oscuro */}
      <div
        className="hidden lg:flex w-1/2 flex-col items-center justify-center p-16 relative overflow-hidden"
        style={{ backgroundColor: '#3B4155' }}
      >
        {/* Círculos de fondo */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#FACF52]/5" />
        <div className="absolute -bottom-32 -right-32 w-[32rem] h-[32rem] rounded-full bg-white/5" />

        {/* Toggle tema — esquina superior derecha del panel */}
        <button
          onClick={toggleTheme}
          title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          className="absolute top-6 right-6 p-2 rounded-xl hover:bg-white/10 transition text-white/60 hover:text-white"
        >
          {isDark ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        <div className="relative z-10 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: '#FACF52' }}>
            <span className="font-extrabold text-2xl" style={{ color: '#3B4155' }}>K</span>
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-3">Kreando</h1>
          <p className="text-white/50 text-lg mb-10">Agentes para WhatsApp API</p>

          {/* Feature bullets */}
          <div className="space-y-4 text-left">
            {[
              'Gestión de conversaciones WhatsApp',
              'Agentes de IA configurables',
              'Tablero Kanban en tiempo real',
            ].map((feat) => (
              <div key={feat} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(250,207,82,0.2)' }}>
                  <svg className="w-3 h-3" fill="none" stroke="#FACF52" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-white/60 text-sm">{feat}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Panel derecho — formulario */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        {/* Toggle tema en mobile (solo visible sin el panel izquierdo) */}
        <button
          onClick={toggleTheme}
          title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          className="lg:hidden absolute top-5 right-5 p-2 rounded-xl hover:bg-brand-light transition text-brand-mid"
        >
          {isDark ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        <div className="w-full max-w-sm">
          {/* Logo mobile */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="logo-k w-9 h-9 rounded-xl flex items-center justify-center">
              <span className="logo-k-letter font-extrabold text-sm">K</span>
            </div>
            <div>
              <p className="font-bold text-brand-dark text-sm">Kreando</p>
              <p className="text-brand-mid text-xs">AI Management</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-brand-dark mb-1">Bienvenido</h2>
          <p className="text-brand-mid text-sm mb-8">Ingresa tus credenciales para continuar</p>

          {/* Error */}
          {error && (
            <div className="mb-5 flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="flex-1">{error}</span>
              <button onClick={clearError} className="text-red-400 hover:text-red-600 font-bold">✕</button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-brand-dark mb-1.5">Usuario</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={isLoading}
                placeholder="tu_usuario"
                className="w-full px-4 py-2.5 bg-brand-light border-0 rounded-xl text-sm text-brand-dark placeholder-brand-mid focus:outline-none focus:ring-2 focus:ring-brand-yellow disabled:opacity-60"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-brand-dark mb-1.5">Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 bg-brand-light border-0 rounded-xl text-sm text-brand-dark placeholder-brand-mid focus:outline-none focus:ring-2 focus:ring-brand-yellow disabled:opacity-60"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="md"
              fullWidth
              loading={isLoading}
              className="mt-2"
            >
              {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>
          </form>

          <p className="text-center text-brand-mid text-xs mt-8">
            Usa tus credenciales para acceder
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
